import { DeleteOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Checkbox, Col, Form, Input, message, Popconfirm, Row, Spin } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useLoginModal } from '../../../../context/loginModalContext'

import { useUser } from '../../../../context/userContext'
import { showAuthError } from '../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../common/CommonStyles'
import EmailField from '../../../common/formFields/EmailField'
import NameField from '../../../common/formFields/NameField'
import PhoneField from '../../../common/formFields/PhoneField'
import {
  ADD_NEW_SUPPLIER,
  DELETE_SUPPLIER,
  EDIT_SUPPLIER
} from '@dentolize/clinic-mobile/src/shared/store/mutations/companyMutations'
import { SEARCH_SUPPLIERS_QUERY } from '@dentolize/clinic-mobile/src/shared/store/queries/appointmentQueries'
import { useForceUpdate } from '../../../../shared/hooks/useForceUpdate'
import { v4 as uuidv4 } from 'uuid'
import CreateNewItemExtraButtons from '../../../common/CreateNewItemExtraButtons'

const SuppliersForm = props => {
  const { setLoginModalVisible } = useLoginModal()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { user } = useUser()
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields, getFieldValue, setFieldsValue } = form

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  useEffect(() => {
    resetFields()
  }, [formVisible])

  const [addNewSupplier, { loading }] = useMutation(ADD_NEW_SUPPLIER, {
    refetchQueries: [{ query: SEARCH_SUPPLIERS_QUERY, variables: { searchTerm: '' } }]
  })
  const [editSupplier, { loading: loadingEdit }] = useMutation(EDIT_SUPPLIER)
  const [deleteSupplier, { loading: loadingDelete }] = useMutation(DELETE_SUPPLIER, {
    update: handleUpdateAfterDelete
  })

  function handleUpdateAfterDelete(store, deletedItem) {
    store.data.delete(`Supplier:${deletedItem.data.deleteSupplier.id}`)
    props.handleAnimateRow && props.handleAnimateRow(null, true)
  }

  const handleSubmit = key => {
    validateFields()
      .then(async values => {
        try {
          let supplier

          if (selectedItem.new) {
            supplier = await addNewSupplier({
              variables: {
                ...values,
                number: values.number ? `${values.suffix || ''}_${values.number}` : ''
              }
            })
            if (key === 'view') {
              navigate(`/associates/supplier/${supplier.data.addNewSupplier.id}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
            } else {
              handleClose(supplier.data.addNewSupplier)
            }
          } else {
            supplier = await editSupplier({
              variables: {
                ...values,
                number: values.number ? `${values.suffix || ''}_${values.number}` : '',
                supplier: selectedItem.id
              }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)
          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (supplier.data.addNewSupplier && supplier.data.addNewSupplier.id) ||
                (supplier.data.editSupplier && supplier.data.editSupplier.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  const handleDelete = async () => {
    try {
      await deleteSupplier({
        variables: {
          supplier: selectedItem.id
        }
      })
      message.success(t('common.deletedSuccess'))
      handleClose()
      navigate('/associates/suppliers')
    } catch (e) {
      showAuthError(e, t, setLoginModalVisible)
    }
  }

  return (
    <StyledDrawer
      title={selectedItem.new ? t('app.newSupplier') : `${t('app.editSupplier')} - ( ${selectedItem.name} )`}
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new && user.permissions.deleteLab ? (
            <Popconfirm
              title={t('common.sureDelete')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              onConfirm={handleDelete}
              disabled={loading || loadingEdit}
            >
              <Button loading={loadingDelete} disabled={loading || loadingEdit} type="primary" danger icon={<DeleteOutlined />}>
                {t('common.delete')}
              </Button>
            </Popconfirm>
          ) : selectedItem.new && !selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmit} disabled={loading} />
          ) : null}
          <Button
            loading={loading || loadingEdit}
            disabled={loadingDelete}
            htmlType="submit"
            type="primary"
            form={`${generatedID}_SupplierForm`}
          >
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingDelete || loadingEdit}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}_SupplierForm`}
          onFinish={handleSubmit}
          initialValues={{
            ...selectedItem,
            number: selectedItem.number?.split('_')[1] || '',
            suffix: selectedItem.number?.split('_').length > 1 ? selectedItem.number.split('_')[0] : user.company.code
          }}
        >
          <Row type="flex" gutter={16}>
            <Col md={12} xs={24}>
              <NameField autoFocus required label={t('common.name')} min={0} />
            </Col>
            <Col md={12} xs={24}>
              <PhoneField
                getFieldValue={getFieldValue}
                setFieldsValue={setFieldsValue}
                label={t('calendar.companyNumber')}
                fieldName="number"
              />
            </Col>
            <Col md={12} xs={24}>
              <EmailField />
            </Col>
            <Col span={24}>
              <Form.Item label={t('common.details')} name="details" rules={[{ max: 1000, message: t('calendar.detailsLong') }]}>
                <Input.TextArea dir="auto" rows={4} maxLength={1000} showCount placeholder={t('calendar.addDetails')} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="disabled" valuePropName="checked">
                <Checkbox>{t('common.disabled')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

SuppliersForm.propTypes = {
  selectedItem: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  formVisible: PropTypes.bool.isRequired,
  handleAnimateRow: PropTypes.func
}

export default SuppliersForm
